import React, { PropsWithChildren, useState, useEffect, useMemo } from "react";

import usePopper from "react-overlays/cjs/usePopper";
import { Portal } from "react-overlays";
import { Rectangle } from "@glideapps/glide-data-grid";
import { overlaysRef } from "src/App";

interface GlideDataGridDropdownWrapperProps {
  handleClickOverlay?: () => void;
  referenceElement: HTMLElement | null;
  cellBounds?: Rectangle;
}

export const dropdownRef = React.createRef<HTMLDivElement>();

// Create a virtual reference element from bounds for Popper positioning
const createVirtualElement = (bounds: Rectangle) => ({
  getBoundingClientRect: () => ({
    width: bounds.width,
    height: bounds.height,
    top: bounds.y,
    right: bounds.x + bounds.width,
    bottom: bounds.y + bounds.height,
    left: bounds.x,
    x: bounds.x,
    y: bounds.y,
    toJSON: () => ({}),
  }),
});

export function GlideDataGridDropdownWrapper({
  children,
  handleClickOverlay,
  referenceElement,
  cellBounds,
}: PropsWithChildren<GlideDataGridDropdownWrapperProps>) {
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );

  // Use virtual element if cellBounds provided, otherwise fall back to referenceElement
  const popperReference = useMemo(() => {
    if (cellBounds) {
      return createVirtualElement(cellBounds);
    }
    return referenceElement;
  }, [cellBounds, referenceElement]);

  const { styles, attributes, update } = usePopper(
    popperReference,
    popperElement,
    {
      placement: "bottom-start",
      strategy: "fixed",
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 8],
          },
        },
        {
          name: "preventOverflow",
          options: {
            boundary: "clippingParents",
            padding: 8,
            altAxis: true,
            tether: false,
            altBoundary: false,
          },
        },
        {
          name: "flip",
          options: {
            fallbackPlacements: ["top-start"],
            padding: 8,
            flipVariations: false,
          },
        },
        {
          name: "computeStyles",
          options: {
            adaptive: true,
            gpuAcceleration: true,
          },
        },
      ],
    },
  );

  // Force Popper to recalculate position after content renders and measures
  useEffect(() => {
    if (popperElement && update) {
      // Schedule update on next frame to ensure content is measured
      requestAnimationFrame(() => {
        update();
      });
    }
  }, [popperElement, children, update]);

  if (!popperReference) return null;

  return (
    <Portal container={overlaysRef}>
      <>
        <div
          className="glide-data-grid__dropdown-overlay"
          onClick={handleClickOverlay}
        />
        <div
          style={styles.popper as React.CSSProperties}
          {...attributes.popper}
          ref={setPopperElement}
        >
          <div className="glide-data-grid__dropdown" ref={dropdownRef}>
            {children}
          </div>
        </div>
      </>
    </Portal>
  );
}
